import { useState, useEffect, useContext, useCallback, useRef } from "react";
import gql from "graphql-tag";
import {
  subscriptionNewMessage,
  sendChatMessage,
  chat,
} from "../apollo/server";
import { useMutation, useQuery } from "@apollo/client";
import UserContext from "../context/User";

export const useChatScreen = (id) => {
  const messageContainerRef = useRef()
  const { profile } = useContext(UserContext);
  const { subscribeToMore: subscribeToMessages, data: chatData } = useQuery(
    gql`
      ${chat}
    `,
    {
      variables: { order: id },
      fetchPolicy: "network-only",
      onError,
    }
  );
  const messageSetter = useCallback(() => {
    if (chatData) {
      setMessages(
        // A failed or empty answer comes back as null, not [].
        (chatData.chat || []).map((message) => ({
          _id: message._id,
          text: message.message,
          createdAt: message.createdAt,
          user: {
            _id: message.user._id,
            name: message.user.name,
          },
        }))
      );
    }
  }, [chatData]);

  useEffect(() => {
    messageSetter();
  }, [chatData, messageSetter]);

  const [send] = useMutation(
    gql`
      ${sendChatMessage}
    `,
    {
      onError,
    }
  );
  function onError(error) {
    alert("Error onError", error.message);
  }
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!id) return;
    const unsubscribe = subscribeToMessages({
      document: gql`
        ${subscriptionNewMessage}
      `,
      variables: { order: id },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        const newMessage = subscriptionData.data.subscriptionNewMessage;
        if (prev.chat.some(m => m._id === newMessage._id)) return prev;
        // Push directly into local messages so the open chat panel updates
        // without waiting on the chatData -> messageSetter chain.
        setMessages(curr => {
          if (curr.some(m => String(m._id) === String(newMessage._id))) return curr;
          const incoming = {
            _id: newMessage._id,
            text: newMessage.message,
            createdAt: newMessage.createdAt,
            user: { _id: newMessage.user._id, name: newMessage.user.name },
          };
          // Drop optimistic temp version if the server-confirmed copy arrived
          const filtered = curr.filter(
            m => !(String(m._id).startsWith('temp-') && m.text === incoming.text && m.user?._id === incoming.user._id)
          );
          return [...filtered, incoming];
        });
        return { chat: [...prev.chat, newMessage] };
      },
    });
    return unsubscribe;
  }, [id, subscribeToMessages]);

  const onSend = (input) => {
    const text = (input || "").trim();
    if (!text) return;
    // Optimistically render the customer's own message immediately so the UI
    // doesn't wait on the subscription round-trip. Backend uses customer:<id>
    // namespacing so we match what comes back.
    const tempId = `temp-${Date.now()}`;
    setMessages(prev => ([
      ...prev,
      {
        _id: tempId,
        text,
        createdAt: new Date(),
        user: { _id: `customer:${profile._id}`, name: profile.name },
      },
    ]));
    send({
      variables: {
        orderId: id,
        messageInput: {
          message: text,
          user: {
            _id: profile._id,
            name: profile.name,
          },
        },
      },
    });
  };

  return {
    messages,
    onSend,
    profile,
    setMessages,
    messageSetter,
    messageContainerRef
  };
};
